/**
 * WHICH BLOCKS A VARIABLE MAY APPEAR IN, at each rung of the Variables capability.
 *
 * Hook-free and on its own, because two very different callers need the same answer and only one
 * of them is a component: `useVariableAffordance` decides whether to offer the insert control,
 * and the issue dispatcher decides whether a chip that is ALREADY THERE is valid. Those must not
 * be allowed to drift, and a shared table is the only way to say so.
 *
 * ## Why the second caller exists at all
 *
 * The affordance gate looked sufficient — a block that cannot be given a variable should never
 * hold one. It is not, and the owner found the hole by walking it: insert a variable in a
 * Numerical block, copy the chip, paste it into an MCQ question. A chip is a node carrying
 * `data-variable-chip`, so the extension's `parseHTML` reconstitutes it on arrival.
 *
 * Typing is a shorter route still, and needs no clipboard. In a FORMULA field a chip is not a
 * node at all — `hasVariableChip` is `/\{[^}]+\}/` over the stored string — and a gated `{` falls
 * through as a literal brace by design, so a teacher types `{dip}` and has a working reference.
 *
 * Neither is grandfathering. Grandfathering protects work authored while the capability was
 * available; this is a workaround at the current rung, and it produces an artifact that cannot be
 * answered: a question whose text varies while its correct answer does not, because the mechanism
 * that would vary the answer lives on the top rung. The stored data is identical either way, so
 * no read-time check can tell a pasted chip from one left behind by a lowered rung. That is
 * exactly why the rule has to be about what is SUPPORTED rather than about how it got there.
 */

/**
 * What each rung adds, as block types. The top rung is absent on purpose: it takes every block,
 * so listing them would be a second roster to keep in step with the block registry.
 *
 * Rung 1 is the Numerical block alone, because there the answer equation MIRRORS the question —
 * `{a} × {b}` asked, `{a}*{b}` answered — and that is the whole idea with nothing else attached.
 *
 * Rung 2 is the rest of the value family. GPS is deliberately NOT in it: its numbers mean a place
 * rather than a quantity, so varying one moves where a student is sent, which reads more like a
 * choice than a measurement.
 *
 * DataViz is NOT here either, and that is a ruling worth keeping: a chart whose data varies can be
 * answered by an MCQ, and then the correct OPTION has to vary with it — which is branching
 * corrects, the thing the top rung exists to gate. The remedy is not bounded by the block holding
 * the variable; it is bounded by whatever block answers about it.
 */
export const RUNG_BLOCKS: Record<number, readonly string[]> = {
  1: ['numerical'],
  2: ['numerical', 'slider', 'compass', 'tilt', 'rotation'],
};

/**
 * May a variable appear in this block type, at this rung?
 *
 * A type the rung does not name is NOT allowed, including a block type added after this table was
 * written. That is the opposite of how `useVariableAffordance` leans, and deliberately so: the
 * affordance fails open because a wrong "no" takes a control from a teacher entitled to it, while
 * this fails closed because a wrong "yes" is the hole it exists to close. The two differ only
 * where the block type is unknown, and a stored block always knows its own type.
 */
export function variableAllowedInBlock(rung: number, blockType: string): boolean {
  if (rung <= 0) return false;
  const allowed = RUNG_BLOCKS[rung];
  // No list for this rung means the top one, which takes every block.
  if (!allowed) return true;
  return allowed.includes(blockType);
}
